// Coherent state / ZIP / city / county handling for the DSCR FIRST-MORTGAGE
// (Optimal Blue Full Search v4) pricer.
//
// Verified live against the provider (2026-09-01):
//   * County is REQUIRED. An empty county returns HTTP 400
//     " - is not a recognized County Name for CO."
//   * A county from another state returns HTTP 400
//     "Broward - is not a recognized County Name for CO."
// So every request must carry a state, an in-state ZIP, and an in-state county.
//
// This module is NOT used by, and must never be imported into, the separate
// DSCR HELOC Excel/rate-sheet engine.

import { states } from "@/data/states";

export type StateLocation = { zipCode: string; city: string; county: string };

/**
 * Provider-valid centroid location per state. ZIP/city/county are consistent
 * with one another so Full Search v4 always accepts them.
 */
const STATE_LOCATION: Record<string, StateLocation> = {
  AL: { zipCode: "35203", city: "Birmingham", county: "Jefferson" },
  AK: { zipCode: "99501", city: "Anchorage", county: "Anchorage" },
  AZ: { zipCode: "85003", city: "Phoenix", county: "Maricopa" },
  AR: { zipCode: "72201", city: "Little Rock", county: "Pulaski" },
  CA: { zipCode: "90012", city: "Los Angeles", county: "Los Angeles" },
  CO: { zipCode: "80202", city: "Denver", county: "Denver" },
  CT: { zipCode: "06103", city: "Hartford", county: "Hartford" },
  DC: { zipCode: "20001", city: "Washington", county: "District of Columbia" },
  DE: { zipCode: "19801", city: "Wilmington", county: "New Castle" },
  FL: { zipCode: "33602", city: "Tampa", county: "Hillsborough" },
  GA: { zipCode: "30303", city: "Atlanta", county: "Fulton" },
  HI: { zipCode: "96813", city: "Honolulu", county: "Honolulu" },
  ID: { zipCode: "83702", city: "Boise", county: "Ada" },
  IL: { zipCode: "60601", city: "Chicago", county: "Cook" },
  IN: { zipCode: "46204", city: "Indianapolis", county: "Marion" },
  IA: { zipCode: "50309", city: "Des Moines", county: "Polk" },
  KS: { zipCode: "67202", city: "Wichita", county: "Sedgwick" },
  KY: { zipCode: "40202", city: "Louisville", county: "Jefferson" },
  LA: { zipCode: "70112", city: "New Orleans", county: "Orleans" },
  ME: { zipCode: "04101", city: "Portland", county: "Cumberland" },
  MD: { zipCode: "21201", city: "Baltimore", county: "Baltimore City" },
  MA: { zipCode: "02108", city: "Boston", county: "Suffolk" },
  MI: { zipCode: "48226", city: "Detroit", county: "Wayne" },
  MN: { zipCode: "55401", city: "Minneapolis", county: "Hennepin" },
  MS: { zipCode: "39201", city: "Jackson", county: "Hinds" },
  MO: { zipCode: "63101", city: "Saint Louis", county: "St. Louis City" },
  MT: { zipCode: "59601", city: "Helena", county: "Lewis and Clark" },
  NE: { zipCode: "68102", city: "Omaha", county: "Douglas" },
  NV: { zipCode: "89101", city: "Las Vegas", county: "Clark" },
  NH: { zipCode: "03101", city: "Manchester", county: "Hillsborough" },
  NJ: { zipCode: "07102", city: "Newark", county: "Essex" },
  NM: { zipCode: "87102", city: "Albuquerque", county: "Bernalillo" },
  NY: { zipCode: "10007", city: "New York", county: "New York" },
  NC: { zipCode: "27601", city: "Raleigh", county: "Wake" },
  ND: { zipCode: "58501", city: "Bismarck", county: "Burleigh" },
  OH: { zipCode: "43215", city: "Columbus", county: "Franklin" },
  OK: { zipCode: "73102", city: "Oklahoma City", county: "Oklahoma" },
  OR: { zipCode: "97204", city: "Portland", county: "Multnomah" },
  PA: { zipCode: "19102", city: "Philadelphia", county: "Philadelphia" },
  RI: { zipCode: "02903", city: "Providence", county: "Providence" },
  SC: { zipCode: "29201", city: "Columbia", county: "Richland" },
  SD: { zipCode: "57501", city: "Pierre", county: "Hughes" },
  TN: { zipCode: "37203", city: "Nashville", county: "Davidson" },
  TX: { zipCode: "75201", city: "Dallas", county: "Dallas" },
  UT: { zipCode: "84111", city: "Salt Lake City", county: "Salt Lake" },
  VT: { zipCode: "05602", city: "Montpelier", county: "Washington" },
  VA: { zipCode: "23219", city: "Richmond", county: "Richmond City" },
  WA: { zipCode: "98101", city: "Seattle", county: "King" },
  WV: { zipCode: "25301", city: "Charleston", county: "Kanawha" },
  WI: { zipCode: "53202", city: "Milwaukee", county: "Milwaukee" },
  WY: { zipCode: "82001", city: "Cheyenne", county: "Laramie" },
};

/** Every state offered in the rate selector must have a provider-valid location. */
export const OFFERED_STATE_ABBREVS = states.map((s) => s.abbrev);

/** Provider-valid centroid location for a state, or null when unknown. */
export function centroidLocationForState(abbrev: string): StateLocation | null {
  return STATE_LOCATION[(abbrev || "").toUpperCase()] ?? null;
}

/** Representative, provider-valid ZIP for a state abbreviation. */
export function centroidZipForState(abbrev: string): string | null {
  return centroidLocationForState(abbrev)?.zipCode ?? null;
}

/** USPS 3-digit ZIP prefix ranges by state. */
const ZIP3_RANGES: Record<string, [number, number][]> = {
  AL: [[350, 369]],
  AK: [[995, 999]],
  AZ: [[850, 865]],
  AR: [[716, 729]],
  CA: [[900, 961]],
  CO: [[800, 816]],
  CT: [[60, 69]],
  DE: [[197, 199]],
  DC: [[200, 200], [202, 205], [569, 569]],
  FL: [[320, 349]],
  GA: [[300, 319], [398, 399]],
  HI: [[967, 968]],
  ID: [[832, 838]],
  IL: [[600, 629]],
  IN: [[460, 479]],
  IA: [[500, 528]],
  KS: [[660, 679]],
  KY: [[400, 427]],
  LA: [[700, 714]],
  ME: [[39, 49]],
  MD: [[206, 219]],
  MA: [[10, 27], [55, 55]],
  MI: [[480, 499]],
  MN: [[550, 567]],
  MS: [[386, 397]],
  MO: [[630, 658]],
  MT: [[590, 599]],
  NE: [[680, 693]],
  NV: [[889, 898]],
  NH: [[30, 38]],
  NJ: [[70, 89]],
  NM: [[870, 884]],
  NY: [[5, 5], [100, 149]],
  NC: [[270, 289]],
  ND: [[580, 588]],
  OH: [[430, 459]],
  OK: [[730, 749]],
  OR: [[970, 979]],
  PA: [[150, 196]],
  RI: [[28, 29]],
  SC: [[290, 299]],
  SD: [[570, 577]],
  TN: [[370, 385]],
  TX: [[750, 799], [885, 885]],
  UT: [[840, 847]],
  VT: [[50, 59]],
  VA: [[201, 201], [220, 246]],
  WA: [[980, 994]],
  WV: [[247, 268]],
  WI: [[530, 549]],
  WY: [[820, 831]],
};

/** State abbreviation a 5-digit ZIP belongs to, or null when unknown. */
export function stateForZip(zip: string): string | null {
  if (!/^\d{5}$/.test(zip)) return null;
  const p = Number(zip.slice(0, 3));
  for (const [abbrev, ranges] of Object.entries(ZIP3_RANGES)) {
    for (const [lo, hi] of ranges) if (p >= lo && p <= hi) return abbrev;
  }
  return null;
}

/**
 * False only when the ZIP is well-formed AND maps to a different state.
 * Unknown/unmapped ZIPs are treated as acceptable (the provider stays the
 * authority); an outright mismatch is caught before any request goes out.
 */
export function zipMatchesState(zip: string, abbrev: string): boolean {
  const owner = stateForZip(zip);
  if (!owner) return true;
  return owner === (abbrev || "").toUpperCase();
}

export type ScenarioLocation = {
  state: string;
  zipCode: string;
  city: string;
  county: string;
};

/**
 * Repair a location so state, ZIP, city and county agree and the provider will
 * accept it. A ZIP belonging to another state is replaced with the state's
 * centroid (city/county too, since they belonged to the old state); a missing
 * county is filled from the state's centroid because the provider requires it.
 */
export function normalizeLocation(loc: ScenarioLocation): ScenarioLocation {
  const state = (loc.state || "").toUpperCase();
  const centroid = centroidLocationForState(state);
  const zipOk = /^\d{5}$/.test(loc.zipCode) && zipMatchesState(loc.zipCode, state);
  if (!zipOk && centroid) return { state, ...centroid };
  const county = loc.county?.trim() || centroid?.county || "";
  const city = loc.city?.trim() || (loc.county?.trim() ? "" : (centroid?.city ?? ""));
  return { state, zipCode: loc.zipCode, city, county };
}

/** Location payload for a state page's embedded pricer. */
export function stateScenarioLocation(abbrev: string): ScenarioLocation {
  const state = (abbrev || "").toUpperCase();
  const centroid = centroidLocationForState(state);
  return { state, zipCode: centroid?.zipCode ?? "", city: centroid?.city ?? "", county: centroid?.county ?? "" };
}
